import React, {
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import EmojiPicker from "../../components/emoji/emojiPicker";
import {
  useMessageReactions,
  setMyReactionOnDoc,
} from "./reactionHelpers";

import "./reactions.scss";

/**
 * Props:
 * - docRef
 * - currentUser { uid }
 * - isSelf (boolean): did currentUser send this message?
 */
export default function ReactionSummary({ docRef, currentUser, isSelf }) {
  const { rxMap, groups, myRx } = useMessageReactions(docRef, currentUser);
  const users = useSelector((state) => state.user.users);

  const [detailsOpen, setDetailsOpen] = useState(false);

  // in-modal picker state (for "Change" next to yourself)
  const [modalPickerOpen, setModalPickerOpen] = useState(false);
  const [modalPickerPos, setModalPickerPos] = useState({ top: 0, left: 0 });
  const [modalPickerVisible, setModalPickerVisible] = useState(false);

  const modalPickerRef = useRef(null);
  const justOpenedPickerRef = useRef(false);

  // map uid -> user
  const getUserInfo = useCallback(
    (uid) => users?.find((u) => u.uid === uid) || null,
    [users]
  );

  // flatten to [{ uid, emoji }]
  const peopleList = Object.entries(rxMap || {}).map(([uid, v]) => ({
    uid,
    emoji: v?.emoji || "",
  }));

  // ---- details (full list) modal helpers ----
  const openDetails = () => setDetailsOpen(true);

  const closeDetails = () => {
    setDetailsOpen(false);
    // also close any open "Change" picker if modal closes
    setModalPickerOpen(false);
    setModalPickerVisible(false);
  };

  // ---- modal picker helpers ----
  const openModalPicker = (btnEl) => {
    if (!btnEl) return;
    justOpenedPickerRef.current = true;
    setModalPickerVisible(false);
    setModalPickerOpen(true);

    // position picker anchored to the "Change" button
    const r = btnEl.getBoundingClientRect();
    const approximatePickerWidth = 300;
    const GAP = 8;

    let top = r.bottom + GAP + window.scrollY;
    let left = r.left + window.scrollX;

    // keep inside viewport horizontally
    const vw = window.innerWidth;
    if (left + approximatePickerWidth > vw - GAP) {
      left = vw - GAP - approximatePickerWidth;
    }
    if (left < GAP) left = GAP;

    setModalPickerPos({ top, left });
  };

  // ---- EFFECTS (fixed so hooks are not conditional) ----

  // After we open the modal picker, wait one frame and then show it
  useLayoutEffect(() => {
    if (modalPickerOpen) {
      requestAnimationFrame(() => {
        setModalPickerVisible(true);
        justOpenedPickerRef.current = false;
      });
    } else {
      // picker closed
      setModalPickerVisible(false);
    }
  }, [modalPickerOpen]);

  // Close the modal picker on outside click (only when picker is open)
  useEffect(() => {
    const handleOutside = (e) => {
      if (!modalPickerOpen) return; // guard

      // if click is inside picker, ignore
      if (modalPickerRef.current?.contains(e.target)) return;

      // otherwise close
      if (!justOpenedPickerRef.current) {
        setModalPickerOpen(false);
        setModalPickerVisible(false);
      }
    };

    document.addEventListener("mousedown", handleOutside, true);
    document.addEventListener("touchstart", handleOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleOutside, true);
      document.removeEventListener("touchstart", handleOutside, true);
    };
  }, [modalPickerOpen]);

  // ---- RENDER ----

  // no reactions at all -> nothing under the bubble
  if (!groups.length) {
    return null;
  }

  return (
    <>
      {/* summary pill under the bubble */}
      <button
        onClick={openDetails}
        onMouseDown={(e) => e.preventDefault()}
        title="See all reactions"
        className="reactionsummary_btn"
        style={{
          display: "inline-flex",
          alignItems: "center",
          position: "relative",
          height: 24,
          paddingLeft: 6,
          paddingRight: 6,
          borderRadius: 12,
          // border: "1px solid var(--border, #ddd)",
          // background: "var(--panel, #fff)",
          cursor: "pointer",

          // placement under bubble:
          marginTop: 0  ,
          marginLeft: isSelf ? "auto" : 50,
          marginRight: isSelf ? 10 : "auto",
          transform: "translateY(-4px)", // little overlap look
          boxShadow: "0 2px 6px rgba(0,0,0,.08)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          {groups.slice(0, 4).map((g, idx) => (
            <span
              key={g.emoji}
              className="reactionsummary_btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                borderRadius: "50%",
                // border: "1px solid #eee",
                fontSize: 20,
                position: "relative",
                marginLeft: idx === 0 ? 0 : -6,
                // background: "#fff",
              }}
            >
              {g.emoji}
            </span>
          ))}
          {groups.length > 4 && (
            <span style={{ fontSize: 10, marginLeft: 4 }}>
              +{groups.length - 4}
            </span>
          )}
          {/* total count */}
          <span style={{ fontSize: 10, marginLeft: 4 }}>
            {groups.reduce((sum, g) => sum + g.count, 0)}
          </span>
        </div>
      </button>

      {/* full details modal */}
      {detailsOpen &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,.25)",
              zIndex: 300,
              display: "grid",
              placeItems: "center",
            }}
            onClick={closeDetails}
          >
            <div
            className="reactionssummary_modal"
              style={{
                width: 340,
                maxHeight: "70vh",
                overflow: "auto",
                // background: "var(--panel, #fff)",
                color: "inherit",
                borderRadius: 12,
                boxShadow: "0 6px 24px rgba(0,0,0,.15)",
                padding: 12,
                position: "relative",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <h4 style={{ margin: 0, fontSize: 16 }}>
                  Reactions (
                  {groups.reduce((s, g) => s + g.count, 0)})
                </h4>
                <button
                  onClick={closeDetails}
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    fontSize: 18,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {/* people list */}
              <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
                {peopleList.length === 0 && (
                  <div style={{ fontSize: 14, opacity: 0.7 }}>
                    No reactions yet.
                  </div>
                )}

                {peopleList.map(({ uid, emoji }) => {
                  const u = getUserInfo(uid);
                  const isMe = uid === currentUser?.uid;

                  return (
                    <div
                      key={uid}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        columnGap: 8,
                        fontSize: 14,
                      }}
                    >
                      {/* left side: avatar + name + emoji all inline */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          columnGap: 8,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        {u?.imageUrl && (
                          <img
                            src={u.imageUrl}
                            alt={u?.userName || uid}
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              objectFit: "cover",
                              flexShrink: 0,
                            }}
                            onError={(e) =>
                              (e.currentTarget.style.display = "none")
                            }
                          />
                        )}

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "nowrap",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            minWidth: 0,
                            width: "100%"
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 500,
                              marginRight: 6,
                              minWidth: 0,
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                            }}
                          >
                            {u?.userName || uid}
                            {isMe ? " (you)" : ""}
                          </span>
                          <span
                            style={{
                              fontSize: 20,
                              lineHeight: 1,
                              marginRight: 6,
                              flexShrink: 0,
                            }}
                          >
                            {emoji}
                          </span>
                        </div>
                      </div>

                      {/* right side buttons for myself */}
                      {isMe && emoji && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            columnGap: 6,
                            flexShrink: 0,
                          }}
                        >
                          <button
                            style={{
                              fontSize: 12,
                              background: "transparent",
                              border: "1px solid var(--border, #ddd)",
                              borderRadius: 6,
                              padding: "4px 6px",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              openModalPicker(e.currentTarget);
                            }}
                          >
                            Change
                          </button>
                          <button
                            style={{
                              fontSize: 12,
                              background: "transparent",
                              border: "none",
                              color: "red",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={async () => {
                              // toggling same emoji removes it
                              await setMyReactionOnDoc(
                                docRef,
                                currentUser?.uid,
                                emoji
                              );
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* in-modal emoji picker for "Change" (only renders if modalPickerOpen) */}
              {modalPickerOpen &&
                createPortal(
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      zIndex: 400,
                    }}
                  >
                    <div
                      ref={modalPickerRef}
                      style={{
                        position: "absolute",
                        top: modalPickerPos.top - 150,
                        left: modalPickerPos.left,
                        visibility: modalPickerVisible ? "visible" : "hidden",
                        zIndex: 401,
                      }}
                    >
                      <EmojiPicker
                        onEmojiSelect={async (emojiObj) => {
                          await setMyReactionOnDoc(
                            docRef,
                            currentUser?.uid,
                            emojiObj.native
                          );
                          setModalPickerOpen(false);
                          setModalPickerVisible(false);
                        }}
                        onClickOutside={() => {
                          if (!justOpenedPickerRef.current) {
                            setModalPickerOpen(false);
                            setModalPickerVisible(false);
                          }
                        }}
                      />
                      {myRx && (
                        <div style={{ marginTop: 6, textAlign: "right" }}>
                          <button
                            style={{
                              fontSize: 12,
                              opacity: 0.8,
                              border: "none",
                              background: "transparent",
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                            onClick={async () => {
                              // same-emoji toggle off
                              await setMyReactionOnDoc(
                                docRef,
                                currentUser?.uid,
                                myRx.emoji
                              );
                              setModalPickerOpen(false);
                              setModalPickerVisible(false);
                            }}
                          >
                            Remove my reaction
                          </button>
                        </div>
                      )}
                    </div>
                  </div>,
                  document.body
                )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
